/**
 * Query Key Factory — типобезопасная фабрика ключей для React Query
 * 
 * Организована по доменам для удобной навигации и инвалидации.
 * Каждый домен имеет:
 * - `all` — базовый ключ для инвалидации всей группы
 * - Методы для создания ключей с параметрами
 * 
 * @example
 * // Использование в хуках
 * useQuery({ queryKey: queryKeyFactory.references.all, ... })
 * useQuery({ queryKey: queryKeyFactory.managerSalesPlans.byBgManagerAndYear(bgId, managerId, year), ... })
 * 
 * // Инвалидация всей группы
 * queryClient.invalidateQueries({ queryKey: queryKeyFactory.managerSalesPlans.all })
 */
export const queryKeyFactory = {
  // ============================================================================
  // SALES PLANNING — Stage 1
  // ============================================================================
  salesPlan: {
    all: ['salesPlan'] as const,
    byYear: (year: number) => ['salesPlan', year] as const,
  },

  businessGroups: {
    all: ['businessGroups'] as const,
    stats: (year?: number) => year !== undefined
      ? ['businessGroupStats', year] as const
      : ['businessGroupStats'] as const,
    periods: (year?: number) => year !== undefined 
      ? ['businessGroupPeriods', year] as const 
      : ['businessGroupPeriods'] as const,
  },

  // ============================================================================
  // SALES PLANNING — Stage 2
  // ============================================================================
  cprTnStats: {
    all: ['cprTnStats'] as const,
    byYear: (year: number) => ['cprTnStats', year] as const,
  },

  tmzStocks: {
    all: ['tmzStocks'] as const,
  },

  factPlanTnCpr: {
    all: ['factPlanTnCpr'] as const,
    byYear: (year: number) => ['factPlanTnCpr', year] as const,
    /**
     * Экраны «Сверки ПП и ПЗ» — агрегаты 2s1m. Под префиксом 2s1m намеренно:
     * WS-событие сохранения 2s1m и смена методологии года инвалидируют их без доп. кода.
     */
    /** Префикс всех `recon(...)` года — для инвалидации после собственного сохранения 2s1m (WS-эхо себе отфильтровано). */
    reconAll: (year: number) => ['factPlanTnCpr', year, 'recon'] as const,
    recon: (year: number, screen: string, level: string) => ['factPlanTnCpr', year, 'recon', screen, level] as const,
  },

  bgCprTnPeriods: {
    all: ['bgCprTnPeriods'] as const,
    byYear: (year: number) => ['bgCprTnPeriods', year] as const,
  },

  // ============================================================================
  // SALES PLANNING — Stage 3
  // ============================================================================
  stage3BgTnCprSkuPlanSales: {
    all: ['stage3BgTnCprSkuPlanSales'] as const,
    byYear: (year: number) => ['stage3BgTnCprSkuPlanSales', year] as const,
  },

  // ============================================================================
  // SALES PLANNING — Stage 4
  // ============================================================================
  businessGroupRevenuePlans: {
    all: ['businessGroupRevenuePlans'] as const,
    byYear: (year: number) => ['businessGroupRevenuePlans', year] as const,
  },

  businessGroupUsers: {
    all: ['businessGroupUsers'] as const,
    byBg: (businessGroupId?: string) => ['businessGroupUsers', businessGroupId] as const,
    byBgAndYear: (businessGroupId: string, year: number) =>
      ['businessGroupUsers', businessGroupId, year] as const,
  },

  userSalesPlans: {
    all: ['userSalesPlans'] as const,
    byBg: (businessGroupId: string) => ['userSalesPlans', businessGroupId] as const,
    byBgAndYear: (businessGroupId: string, year: number) => ['userSalesPlans', businessGroupId, year] as const,
  },

  userSalesPlansAccepted: {
    all: ['userSalesPlansAccepted'] as const,
    byBg: (businessGroupId: string) => ['userSalesPlansAccepted', businessGroupId] as const,
    byBgAndYear: (businessGroupId: string, year: number) =>
      ['userSalesPlansAccepted', businessGroupId, year] as const,
  },

  managerRevenueTotals: {
    all: ['managerRevenueTotals'] as const,
    byBg: (businessGroupId: string) => ['managerRevenueTotals', businessGroupId] as const,
    byBgAndYear: (businessGroupId: string, year: number) =>
      ['managerRevenueTotals', businessGroupId, year] as const,
  },

  stage4MpDistribution: {
    all: ['stage4MpDistribution'] as const,
  },

  acceptPlanStage4: {
    all: ['acceptPlanStage4'] as const,
    versions: (businessGroupId: string, year: number) =>
      ['acceptPlanStage4', 'versions', businessGroupId, year] as const,
    versionData: (version: string, businessGroupId: string, year: number) =>
      ['acceptPlanStage4', 'data', version, businessGroupId, year] as const,
  },

  /** Утверждение 4s2m → версии 4s2s (распределение по МП в разрезе ЦПР-ТН-SKU) */
  acceptPlanStage4Mp: {
    all: ['acceptPlanStage4Mp'] as const,
    statusByBg: (businessGroupId: string) => ['acceptPlanStage4Mp', 'status', businessGroupId] as const,
    status: (businessGroupId: string, year: number) =>
      ['acceptPlanStage4Mp', 'status', businessGroupId, year] as const,
    versions: (businessGroupId: string, year: number) =>
      ['acceptPlanStage4Mp', 'versions', businessGroupId, year] as const,
    versionData: (businessGroupId: string, year: number, version: number | null) =>
      ['acceptPlanStage4Mp', 'data', businessGroupId, year, version] as const,
  },

  // ============================================================================
  // SALES PLANNING — Stage 5
  // ============================================================================
  managerSalesPlans: {
    all: ['managerSalesPlans'] as const,
    byBg: (businessGroupId: string | null) => 
      ['managerSalesPlans', businessGroupId] as const,
    byBgAndManager: (businessGroupId: string | null, managerId: string | null) => 
      ['managerSalesPlans', businessGroupId, managerId] as const,
    byBgManagerAndYear: (
      businessGroupId: string | null,
      managerId: string | null,
      year: number,
    ) => ['managerSalesPlans', businessGroupId, managerId, year] as const,
  },

  stage5UserContext: {
    all: ['stage5UserContext'] as const,
  },

  partnerTnCprStat: {
    all: ['partnerTnCprStat'] as const,
    byParams: (businessGroupId: string | null, managerId: string | null, partnerId: string | null, year: number) =>
      ['partnerTnCprStat', businessGroupId, managerId, partnerId, year] as const,
  },

  acceptPlanSales: {
    all: ['acceptPlanSales'] as const,
    versions: (year: number, businessGroupId: string | null) =>
      ['acceptPlanSales', 'versions', year, businessGroupId] as const,
    versionData: (version: string, year: number, businessGroupId: string | null) =>
      ['acceptPlanSales', 'data', version, year, businessGroupId] as const,
  },

  dynamicPlanSales: {
    all: ['dynamicPlanSales'] as const,
    byYear: (year: number) => ['dynamicPlanSales', year] as const,
  },

  periodsPlanSales: {
    all: ['periodsPlanSales'] as const,
  },

  periodsSummary: {
    all: ['periodsSummary'] as const,
  },

  salesCpr: {
    all: ['salesCpr'] as const,
    byYear: (year: number) => ['salesCpr', year] as const,
  },

  // ============================================================================
  // REFERENCES
  // ============================================================================
  references: {
    all: ['references'] as const,
    baseStats: () => ['references-base-stats'] as const,
  },

  keyAssortment: {
    all: ['keyAssortment'] as const,
    bySourceAndYear: (source?: string, year?: number) => 
      ['keyAssortment', source, year] as const,
    stats: () => ['references-key-assortment-stats'] as const,
  },

  suppliersKnp: {
    all: ['suppliers-knp'] as const,
    byYear: (year?: number) => ['suppliers-knp', year] as const,
    stats: () => ['references-suppliers-knp-stats'] as const,
  },

  pricePlanning: {
    all: ['pricePlanning'] as const,
    byYear: (year: number) => ['pricePlanning', year] as const,
    stats: () => ['references-price-planning-stats'] as const,
  },

  priceGram: {
    all: ['priceGram'] as const,
    byYear: (year: number) => ['priceGram', year] as const,
    stats: () => ['references-price-planning-gram-stats'] as const,
  },

  shipmentPercentage: {
    all: ['shipmentPercentage'] as const,
    byYear: (year: number) => ['shipmentPercentage', year] as const,
    stats: () => ['references-shipment-stats'] as const,
  },

  salesDistribution: {
    all: ['salesDistribution'] as const,
    byYear: (year: number) => ['salesDistribution', year] as const,
  },

  pageComments: {
    all: ['pageComments'] as const,
    byKey: (pageKey: string) => ['pageComments', pageKey] as const,
    admin: () => ['pageComments', 'admin'] as const,
  },

  /** Методология расчёта этапов 2–4 (2026/2027) — не путать с вики `methodology`. */
  calcMethodology: {
    all: ['calcMethodology'] as const,
    state: (year: number) => ['calcMethodology', 'state', year] as const,
    log: (year: number) => ['calcMethodology', 'log', year] as const,
  },

  methodology: {
    all: ['methodology'] as const,
    tree: () => ['methodology', 'tree'] as const,
    page: (id: string) => ['methodology', 'page', id] as const,
  },

  priceCorrectionPack: {
    all: ['priceCorrectionPack'] as const,
    byYear: (year: number) => ['priceCorrectionPack', year] as const,
  },

  priceCorrectionGram: {
    all: ['priceCorrectionGram'] as const,
    byYear: (year: number) => ['priceCorrectionGram', year] as const,
  },

  clientManagement: {
    all: ['clientManagement'] as const,
    byYear: (year: number) => ['clientManagement', year] as const,
    stats: () => ['references-client-management-stats'] as const,
  },

  approvalDate: {
    all: ['approvalDate'] as const,
    byYear: (year: number) => ['approvalDate', year] as const,
  },

  acceptStage3Plan: {
    all: ['acceptStage3Plan'] as const,
    status: (year: number) => ['acceptStage3Plan', 'status', year] as const,
    versions: (year: number) => ['acceptStage3Plan', 'versions', year] as const,
    versionData: (year: number, version: number | null) => ['acceptStage3Plan', 'data', year, version] as const,
  },

  actualizationDeadlines: {
    all: ['actualizationDeadlines'] as const,
    byYear: (year: number) => ['actualizationDeadlines', year] as const,
  },

  actualizationClicks: {
    all: ['actualizationClicks'] as const,
    byYearAndBg: (year: number, businessGroupId?: string | null) =>
      ['actualizationClicks', year, businessGroupId ?? 'all'] as const,
  },

  // ============================================================================
  // PURCHASE PLANNING
  // ============================================================================
  purchasePlan: {
    all: ['purchasePlan'] as const,
  },

  purchasePeriods: {
    all: ['cprTnPurchasePeriods'] as const,
  },

  purchaseAnalytics: {
    all: ['cprTnPurchaseAnalytics'] as const,
  },

  purchase2s2d: {
    all: ['cprTnPurchase2s2d'] as const,
  },

  // Отдельный корень: WS-инвалидация 2s2d (cprTnPurchase2s2d) не должна
  // перезапрашивать тяжёлую агрегацию 5s1d.
  purchase2s2dSalesTmz: {
    all: ['purchase2s2dSalesTmz'] as const,
    byYear: (year: number) => ['purchase2s2dSalesTmz', year] as const,
  },

  // План закупок по периодам 2s8m. Корень совпадает с WS-ключом бэка
  // (WS_KEY_2S8M): fill/save 2s8m и сохранение 2s1m инвалидируют его.
  purchase2s8m: {
    all: ['purchase2s8m'] as const,
    byYear: (year: number) => ['purchase2s8m', year] as const,
  },

  acceptProcurementPeriod: {
    all: ['acceptProcurementPeriod'] as const,
    byYear: (year: number) => ['acceptProcurementPeriod', 'list', year] as const,
    versionData: (year: number, version: string) => ['acceptProcurementPeriod', 'data', year, version] as const,
  },

  purchaseOverview: {
    all: ['purchaseOverview'] as const,
  },

  acceptPlanPurchase: {
    all: ['acceptPlanPurchase'] as const,
    byVersion: (version: string) => ['acceptPlanPurchase', version] as const,
    versionData: (version: string) => ['acceptPlanPurchase', 'data', version] as const,
  },

  dynamicPlanPurchase: {
    all: ['dynamicPlanPurchase'] as const,
    byYear: (year: number) => ['dynamicPlanPurchase', year] as const,
  },

  periodControl: {
    all: ['periodControl'] as const,
    byYear: (year: number) => ['periodControl', year] as const,
  },

  dynPlanCprTnSku: {
    all: ['dynPlanCprTnSku'] as const,
    byBgAndYear: (businessGroupId: string | null, year: number, activeFilter?: string) =>
      ['dynPlanCprTnSku', businessGroupId, year, activeFilter ?? 'all'] as const,
  },

  dpzDppTmz: {
    all: ['dpzDppTmz'] as const,
    byYear: (year: number) => ['dpzDppTmz', year] as const,
  },

  // ============================================================================
  // RBAC & ADMIN
  // ============================================================================
  rbac: {
    all: ['rbac'] as const,
    currentUser: (userOid?: string | null) => ['rbac', 'currentUser', userOid] as const,
    users: () => ['rbac-users'] as const,
    userDetail: (userId: string) => ['rbac-users', 'detail', userId] as const,
    roles: () => ['rbac-roles'] as const,
    resources: () => ['rbac-resources'] as const,
    permissions: () => ['rbac-permissions'] as const,
    stats: () => ['rbac-stats'] as const,
    audit: () => ['rbac-audit'] as const,
    auditRecent: () => ['rbac-audit-recent'] as const,
  },

  admin: {
    monitoring: () => ['admin-monitoring'] as const,
    emailNotifications: {
      all: ['admin-email-notifications'] as const,
      status: () => ['admin-email-notifications', 'status'] as const,
      logsAll: () => ['admin-email-notifications', 'logs'] as const,
      logs: (page: number, status?: string) =>
        ['admin-email-notifications', 'logs', page, status ?? 'all'] as const,
      deliveries: (logId: string) => ['admin-email-notifications', 'deliveries', logId] as const,
    },
    emailTemplates: {
      all: ['admin-email-templates'] as const,
      list: () => ['admin-email-templates', 'list'] as const,
      history: (eventType: string) => ['admin-email-templates', 'history', eventType] as const,
      preview: (eventType: string, subject: string, html: string) =>
        ['admin-email-templates', 'preview', eventType, subject, html] as const,
    },
    priceWatch: {
      all: ['admin-price-watch'] as const,
      settings: () => ['admin-price-watch', 'settings'] as const,
      eventsAll: () => ['admin-price-watch', 'events'] as const,
      events: (params: {
        page: number;
        limit: number;
        source?: string;
        status?: string;
        search?: string;
        from?: string;
        to?: string;
      }) => ['admin-price-watch', 'events', params] as const,
    },
    errors: {
      all: ['admin-errors'] as const,
      exceptions: (source: string, timeRange: string, page: number) =>
        ['admin-errors', 'exceptions', source, timeRange, page] as const,
      detail: (itemId: string) => ['admin-errors', 'detail', itemId] as const,
      stats: (timeRange: string) => ['admin-errors', 'stats', timeRange] as const,
      search: (correlationId: string) => ['admin-errors', 'search', correlationId] as const,
    },
  },

  // ============================================================================
  // FEATURE FLAGS
  // ============================================================================
  featureFlags: {
    all: ['feature-flags'] as const,
    public: () => ['feature-flags', 'public'] as const,
    admin: () => ['feature-flags', 'admin'] as const,
  },

  // ============================================================================
  // UI CONFIG
  // ============================================================================
  uiConfig: {
    all: ['ui-config'] as const,
    bySection: (section: string) => ['ui-config', section] as const,
    history: (section: string, key: string) => ['ui-config', 'history', section, key] as const,
  },
} as const;

/**
 * Типы для query keys
 */
export type QueryKeyFactory = typeof queryKeyFactory;

/**
 * Извлечение типа ключа из фабрики
 * @example
 * type ManagerSalesPlansKey = InferQueryKey<typeof queryKeyFactory.managerSalesPlans.byBgManagerAndYear>;
 * // => readonly ['managerSalesPlans', string | null, string | null, number]
 */
export type InferQueryKey<T extends (...args: never[]) => readonly unknown[]> = ReturnType<T>;
